/**
 * useChatV2Store - Zustand store for Chat V2 state
 *
 * Manages conversation list, active agent, and UI state
 * separate from the message streaming (handled by useChat hook).
 */

import { create } from 'zustand'
import type { Conversation, AgentId } from '../types'
import { DEFAULT_AGENT } from '../constants'
import { chatApiV2 } from '../api/chatApiV2'

interface ChatV2State {
  // Conversations
  conversations: Conversation[]
  currentConversationId: string | null
  isLoadingConversations: boolean

  // Agent
  activeAgent: AgentId

  // Chat toggle (fs-516) — session-scoped boolean sent on each message.
  // entireDataCatalog: expose tenant-wide UKG tools + broaden retrieval
  //   beyond the current board.
  entireDataCatalog: boolean

  // UI State
  isSidebarCollapsed: boolean

  // Actions
  setConversations: (conversations: Conversation[]) => void
  setCurrentConversationId: (id: string | null) => void
  setActiveAgent: (agent: AgentId) => void
  setEntireDataCatalog: (value: boolean) => void
  setIsSidebarCollapsed: (collapsed: boolean) => void

  // Async actions
  loadConversations: (contextId: string) => Promise<void>
  createConversation: (
    contextId: string,
    contextType: 'workspace' | 'document'
  ) => Promise<string>
  deleteConversation: (conversationId: string) => Promise<void>
  renameConversation: (conversationId: string, newName: string) => Promise<void>
  updateConversationFirstMessage: (conversationId: string, firstMessage: string) => void
  updateConversationActiveAgent: (conversationId: string, activeAgent: AgentId) => void

  // Reset
  reset: () => void
}

const initialState = {
  conversations: [],
  currentConversationId: null,
  isLoadingConversations: false,
  activeAgent: DEFAULT_AGENT as AgentId,
  entireDataCatalog: false,
  isSidebarCollapsed: false,
}

export const useChatV2Store = create<ChatV2State>((set, get) => ({
  ...initialState,

  // Setters
  setConversations: (conversations) => set({ conversations }),

  setCurrentConversationId: (id) => set({ currentConversationId: id }),

  setActiveAgent: (agent) => set({ activeAgent: agent }),

  setEntireDataCatalog: (value) => set({ entireDataCatalog: value }),

  setIsSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),

  // Load conversations for a context
  loadConversations: async (contextId: string) => {
    set({ isLoadingConversations: true })

    try {
      const conversations = await chatApiV2.listConversations(contextId)
      // Sort by updatedAt descending (latest first)
      const sortedConversations = [...conversations].sort(
        (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
      )
      set({
        conversations: sortedConversations,
        isLoadingConversations: false,
      })
    } catch (error) {
      console.error('Failed to load conversations:', error)
      set({ isLoadingConversations: false })
    }
  },

  // Create a new conversation
  createConversation: async (
    contextId: string,
    contextType: 'workspace' | 'document'
  ) => {
    const { activeAgent } = get()

    try {
      const response = await chatApiV2.createConversation({
        contextId,
        contextType,
      })

      const newConversation: Conversation = {
        id: response.conversationId,
        contextId,
        contextType,
        createdAt: new Date(),
        updatedAt: new Date(),
        firstMessage: '',
        activeAgent,
      }

      set((state) => ({
        conversations: [newConversation, ...state.conversations],
        // Don't set currentConversationId here - let the hook manage it
        // to avoid race condition with message loading
      }))

      return response.conversationId
    } catch (error) {
      console.error('Failed to create conversation:', error)
      throw error
    }
  },

  // Delete a conversation
  deleteConversation: async (conversationId: string) => {
    try {
      await chatApiV2.deleteConversation(conversationId)

      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== conversationId),
        currentConversationId:
          state.currentConversationId === conversationId
            ? null
            : state.currentConversationId,
      }))
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      throw error
    }
  },

  // Rename a conversation
  renameConversation: async (conversationId: string, newName: string) => {
    try {
      await chatApiV2.renameConversation(conversationId, newName)

      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId
            ? { ...c, firstMessage: newName, updatedAt: new Date() }
            : c
        ),
      }))
    } catch (error) {
      console.error('Failed to rename conversation:', error)
      throw error
    }
  },

  // Update conversation's first message
  updateConversationFirstMessage: (conversationId: string, firstMessage: string) => {
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              firstMessage: firstMessage.slice(0, 60), // Truncate for display
              updatedAt: new Date(),
            }
          : c
      ),
    }))
  },

  // Update conversation's active agent (e.g., when backend routes to a different agent)
  updateConversationActiveAgent: (conversationId: string, activeAgent: AgentId) => {
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              activeAgent,
              updatedAt: new Date(),
            }
          : c
      ),
    }))
  },

  // Reset to initial state
  reset: () => set(initialState),
}))

export default useChatV2Store
